import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { getDocumentPath } from '../utils/document-panel-paths';
import {
  applyVersionInspectorSearchParams,
  parseVersionInspectorSearchParams,
  type DocumentVersionListItem,
} from '../utils/document-version-navigation';
import { VersionInspector, type VersionInspectState } from './VersionInspector';

export const CommenterVersionComparison: React.FC = () => {
  const { projectId, documentId } = useParams<{ projectId?: string; documentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLocale();
  const urlState = useMemo(
    () => parseVersionInspectorSearchParams(searchParams),
    [searchParams],
  );
  const [lockedCompareVersionId] = useState(urlState.compareVersionId);

  const documentPath = documentId && documentId !== 'new'
    ? getDocumentPath(projectId, documentId)
    : '/desk';

  const handleClose = useCallback((): void => {
    navigate(documentPath);
  }, [navigate, documentPath]);

  const handleInspectStateChange = useCallback((state: VersionInspectState): void => {
    setSearchParams((previous) => {
      const next = applyVersionInspectorSearchParams(
        previous,
        state.versionId
          ? {
              versionId: state.versionId,
              compareVersionId: state.mode === 'changes'
                ? (state.compareVersionId ?? lockedCompareVersionId)
                : null,
              mode: state.mode,
            }
          : null,
      );
      return next.toString() === previous.toString() ? previous : next;
    }, { replace: true });
  }, [lockedCompareVersionId, setSearchParams]);

  if (!documentId || documentId === 'new' || !urlState.versionId) {
    return (
      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '1rem' }}>
          {t('versionHistory.loadVersionError')}
        </div>
        <button
          type="button"
          onClick={handleClose}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'var(--scribane-black)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          {t('back_to_desk') || 'Back to Desk'}
        </button>
      </div>
    );
  }

  const versions: DocumentVersionListItem[] = [];

  return (
    <VersionInspector
      isOpen={true}
      onClose={handleClose}
      documentId={documentId}
      versions={versions}
      versionId={urlState.versionId}
      onVersionIdChange={() => undefined}
      initialMode={urlState.mode ?? 'changes'}
      initialCompareVersionId={lockedCompareVersionId}
      onInspectStateChange={handleInspectStateChange}
      allowCompareSelection={false}
      allowVersionNavigation={false}
    />
  );
};
